<template>
  <v-select
    v-bind="$attrs"
    v-model="field.value"
    :loading="loading"
    :items="items"
    item-value="security_mark_id"
    item-text="security_mark_key"
    @change="$emit('change', $event); selectedMark = field.value"
    attach
  >
    <template
      v-slot:append-outer
    >
      <v-text-field
        v-show="false"
        :name="`${field.name}_key`"
        :value="selectedMarkKey"
      >
      </v-text-field>

      <v-checkbox
        v-if="activateFieldCheck"
        v-model="checkedFields"
        :value="field.name"
        :on-icon="activateFieldIcon"
        class="prepend-checkbox"
      ></v-checkbox>
    </template>
  </v-select>
</template>

<script>
  import { mapActions } from 'vuex'
  import securityApi from '@/services/api/node/security'

  export default {
    name: "ClassificationField",

    props: {
      field: {
        type: Object,
        default: () => {}
      },
      nodeIds: {
        type: Array,
        default: () => []
      },
      activateFieldCheck: {
        type: Boolean,
        default: false
      },
      activateFieldIcon: {
        type: String,
        default: "$checkboxOn"
      }
    },

    data: () => ({
      loading: false,
      items: [],
      originalValue: null,
      selectedMark: null,
    }),

    mounted() {
      const parsedValue = parseInt(this.field.value)
      this.field.value = Number.isNaN(parsedValue) ? null : parsedValue

      this.originalValue = this.field.value
      this.selectedMark = this.field.value

      this.fetchData()
    },

    computed: {
      selectedMarkKey() {
        return this.items.find(m => m.security_mark_id == this.selectedMark)?.security_mark_key ?? null
      },
    },

    methods: {
      ...mapActions('operation/security_group', [
        'getAllSecurityMarksBySecurityGroupUser'
      ]),

      ...mapActions('operation/classification', [
        'saveSecurityClassificationPerNode',
        'clearSecurityClassificationPerNode'
      ]),

      ...mapActions('operation/clearance', [
        'getUserGroupsClearances'
      ]),

      async fetchData() {
        this.loading = true

        try {
          const result = await this.getAllSecurityMarksBySecurityGroupUser(this.field.security_group_id)
          const marks = Array.isArray(result) ? result : (result?.items || [])
          const clearances = await this.getUserGroupsClearances()
          const categories = new Map()

          if (this.field.mark_category_key) {
            // Resolve by the mark's category ID, avoiding ambiguous names across tenants.
            const categoryIds = [...new Set(marks
              .filter(mark => mark.mark_category_key == null && mark.mark_category_id != null)
              .map(mark => mark.mark_category_id))]

            await Promise.all(categoryIds.map(async id => {
              try {
                const response = await securityApi.getMarkCategory(id)
                categories.set(String(id), response.data.mark_category_key)
              } catch (error) {
                // An unresolved category must not bypass the category filter.
                console.warn('ClassificationField category lookup failed', id)
              }
            }))
          }

          this.items = [
            { security_mark_id: null, security_mark_key: "—" },
            ...this.filterByTreePath(marks, clearances, categories)
          ]
        } catch (error) {
          this.items = []
          console.warn('ClassificationField loading failed', error?.message || error)
        } finally {
          this.loading = false
        }
      },

      filterByTreePath(marks, clearances, categories = new Map()) {
        const allowedIds = new Set();

        const filterIds = new Set(clearances.map(c => c.security_mark_id));

        marks.forEach(mark => {
          if (filterIds.has(mark.security_mark_id)) {
            allowedIds.add(mark.security_mark_id);

            if (mark.tree_path_down) {
              mark.tree_path_down.split(",").forEach(id => {
                allowedIds.add(Number(id));
              })
            }
          }
        })

        marks = marks.filter(mark => allowedIds.has(mark.security_mark_id))

        if (this.field.mark_category_key) {
          marks = marks.filter(m =>
            (m.mark_category_key ?? categories.get(String(m.mark_category_id))) == this.field.mark_category_key
          )
        }

        return marks.filter(mark => allowedIds.has(mark.security_mark_id));
      },

      async postSubmitAction() {
        const selected = this.field.value

        if (selected) {
          await this.applyClassificationMarksToNodes(selected)

          return
        }

        await this.clearClassificationMarksForNodes()
      },

      async applyClassificationMarksToNodes(mark) {
        if (this.originalValue) {
          await this.clearClassificationMarksForNodes()
        }

        try {
          const payload = this.nodeIds.map(nodeId => ({ nodeId, marks: [mark] }))

          await this.saveSecurityClassificationPerNode(payload)
        } catch (e) {
          console.warn('applyClassificationMarksToNodes error', e?.message || e)
        }
      },

      async clearClassificationMarksForNodes() {
        if (! this.originalValue) {
          return
        }

        try {
          const payload = this.nodeIds.map(id => ({ nodeId: id, marks: [this.originalValue] }))

          await this.clearSecurityClassificationPerNode(payload)
        } catch (e) {
          console.warn('clearClassificationMarksForNodes error', e?.message || e)
        }
      },
    },
  }
</script>
